/**
 * Job Service - API integration for job-related operations
 * Handles job data fetching with proper authentication and error handling
 */

import axios from 'axios';
import logger from '../utils/logger';

// Get API base URL
const getApiBase = () => {
  return process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
};

// Get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Fetches jobs with filtering and pagination
 * @param {Object} options - Query parameters
 * @returns {Promise<Object>} Jobs data with pagination
 */
export const fetchJobs = async (options = {}) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    
    const params = {
      tenantId: options.tenantId || 1,
      cityCode: options.cityCode || 'MCO',
      limit: options.limit || 100,
      page: options.page || 1,
      ...options
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    const response = await axios.get(`${apiBase}/api/jobs/getJobs`, {
      headers,
      params
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching jobs:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch jobs. Please try again.'
    );
  }
};

/**
 * Fetches jobs belonging to the logged-in CLIENT user
 * @returns {Promise<Array>} Jobs for the current client
 */
export const fetchMyJobs = async () => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    const response = await axios.get(`${apiBase}/api/jobs/my-jobs`, { headers });

    return response.data.jobs || [];
  } catch (error) {
    logger.log('Error fetching my jobs:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Failed to fetch your jobs. Please try again.'
    );
  }
};

/**
 * Fetches a single job by ID
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job data
 */
export const fetchJobById = async (jobId) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    const response = await axios.get(`${apiBase}/api/jobs/${jobId}`, {
      headers
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching job:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch job details. Please try again.'
    );
  }
};

/**
 * Fetches jobs for a specific maid
 * @param {string} maidId - Maid user ID
 * @param {Object} options - Additional query options
 * @returns {Promise<Array>} Jobs assigned to the maid
 */
export const fetchJobsForMaid = async (maidId, options = {}) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    
    const params = {
      status: options.status,
      ...options
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    const response = await axios.get(`${apiBase}/api/jobs/maid/${maidId}`, {
      headers,
      params
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching maid jobs:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch technician jobs. Please try again.'
    );
  }
};

/**
 * Fetches job dashboard statistics
 * @param {Object} options - Query parameters
 * @returns {Promise<Object>} Dashboard statistics
 */
export const fetchJobStats = async (options = {}) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    
    const params = {
      tenantId: options.tenantId || process.env.REACT_APP_TENANT_ID || 1,
      cityCode: options.cityCode || process.env.REACT_APP_CITY_CODE || 'MCO',
      ...options
    };

    const response = await axios.get(`${apiBase}/api/jobs/dashboard/stats`, {
      headers,
      params
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching job stats:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch job statistics. Please try again.'
    );
  }
};

/**
 * Fetches jobs with location data suitable for mapping
 * Includes booking address information for geocoding
 * @param {Object} options - Query parameters
 * @returns {Promise<Array>} Jobs with booking/address data
 */
export const fetchJobsForMapping = async (options = {}) => {
  try {
    const jobsData = await fetchJobs({
      ...options,
      // Include booking data for addresses
      include: 'booking,assignments',
      limit: options.limit || 100, // Higher limit for mapping
      // Pass through date range parameters
      scheduledStartMin: options.scheduledStartMin,
      scheduledStartMax: options.scheduledStartMax
    });

    // Extract jobs from the response structure
    const jobs = jobsData.data?.jobs || jobsData.jobs || [];
    logger.log(`Extracted ${jobs.length} jobs from API response`);
    
    // Don't filter jobs here - let the component handle address extraction and filtering
    // This allows the component to use the same logic as ServiceSchedulePage
    logger.log(`Returning all ${jobs.length} jobs for component-level processing`);
    return jobs;
  } catch (error) {
    logger.log('Error fetching jobs for mapping:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch job locations. Please try again.'
    );
  }
};

/**
 * Utility function to extract address string from job data
 * @param {Object} job - Job object from API response
 * @returns {string|null} Address string for geocoding
 */
export const extractJobAddress = (job) => {
  if (!job) return null;

  logger.log(`Extracting address for job ${job.id}:`, {
    hasAddress: !!job.address,
    addressType: typeof job.address,
    addressValue: job.address,
    hasBooking: !!job.booking,
    hasCustomer: !!job.customer
  });

  // Primary: Use the formatted address from the API response
  if (job.address && job.address.formatted) {
    return job.address.formatted;
  }
  
  // Fallback: If address is an object, try to construct full address
  if (job.address && typeof job.address === 'object') {
    const addr = job.address;
    const parts = [
      addr.street,
      addr.street_line_2,
      addr.city,
      addr.state,
      addr.zip
    ].filter(Boolean);
    
    if (parts.length > 0) {
      return parts.join(', ');
    }
  }
  
  // Try booking address if job address is not available
  if (job.booking && job.booking.address) {
    const bookingAddr = job.booking.address;
    if (typeof bookingAddr === 'string') {
      return bookingAddr;
    }
    if (typeof bookingAddr === 'object') {
      const parts = [
        bookingAddr.street,
        bookingAddr.street_line_2,
        bookingAddr.city,
        bookingAddr.state,
        bookingAddr.zip
      ].filter(Boolean);
      
      if (parts.length > 0) {
        return parts.join(', ');
      }
    }
  }
  
  // Try customer address if available
  if (job.customer && job.customer.address) {
    const customerAddr = job.customer.address;
    if (typeof customerAddr === 'string') {
      return customerAddr;
    }
    if (typeof customerAddr === 'object') {
      const parts = [
        customerAddr.street,
        customerAddr.street_line_2,
        customerAddr.city,
        customerAddr.state,
        customerAddr.zip
      ].filter(Boolean);
      
      if (parts.length > 0) {
        return parts.join(', ');
      }
    }
  }
  
  // Last resort: If address is a simple string
  if (job.address && typeof job.address === 'string') {
    return job.address;
  }
  
  logger.log('No valid address found for job:', job.id);
  return null;
};

/**
 * Fetches jobs for a specific employee from HCP API
 * @param {string} employeeId - HCP Employee ID (e.g., pro_c8de8e00622b468292d8ba191a7dc5bb)
 * @param {Object} options - Additional query options
 * @param {string} options.work_status - Filter by work status (completed, in_progress, etc.)
 * @param {string} options.scheduledStartMin - Start date filter (ISO string)
 * @param {string} options.scheduledStartMax - End date filter (ISO string)
 * @param {number} options.limit - Number of jobs to fetch
 * @returns {Promise<Object>} Jobs data for the specific employee
 */
export const fetchJobsForEmployee = async (employeeId, options = {}) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    
    // Build query parameters with employee filter
    const params = {
      employee_ids: [employeeId], // Array format for HCP API
      tenantId: options.tenantId || process.env.REACT_APP_TENANT_ID || 1,
      limit: options.limit || 100,
      work_status: options.work_status,
      scheduledStartMin: options.scheduledStartMin,
      scheduledStartMax: options.scheduledStartMax,
      sort_by: options.sort_by || 'scheduled_start',
      sort_direction: options.sort_direction || 'asc',
      ...options
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    logger.log(`Fetching jobs for employee ${employeeId} with params:`, params);

    const response = await axios.get(`${apiBase}/api/jobs`, {
      headers,
      params
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching jobs for employee:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch employee jobs. Please try again.'
    );
  }
};

/**
 * Fetches employees from the backend API
 * @param {Object} options - Query parameters and options
 * @param {number} options.tenantId - Tenant ID filter
 * @param {number} options.page - Page number for pagination
 * @param {number} options.limit - Number of items per page
 * @returns {Promise<Object>} Employees data with pagination info
 */
export const fetchEmployees = async (options = {}) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    
    // Build query parameters
    const params = {
      tenantId: options.tenantId || process.env.REACT_APP_TENANT_ID || 1,
      page: options.page || 1,
      limit: options.limit || 50,
      ...options
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    const response = await axios.get(`${apiBase}/api/employees`, {
      headers,
      params
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching employees:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Failed to fetch employees. Please try again.'
    );
  }
};

/**
 * Utility function to get job status color for map markers
 * @param {string} status - Job status
 * @returns {string} Color hex code
 */
export const getJobStatusColor = (status) => {
  const statusColors = {
    'NOT_STARTED': '#FFA500', // Orange
    'IN_PROGRESS': '#4CAF50', // Green  
    'COMPLETED': '#2196F3',   // Blue
    'CANCELLED': '#F44336',   // Red
    'PENDING': '#FF9800',     // Amber
    'CONFIRMED': '#8BC34A',   // Light Green
    'TECH_EN_ROUTE': '#3F51B5', // Indigo (customer-facing, derived)
    'RESIDENTIAL': '#9C27B0', // Purple (for job type)
    'COMMERCIAL': '#FF5722',  // Deep Orange
    'VACATION_RENTAL': '#00BCD4', // Cyan
  };
  
  return statusColors[status] || '#9E9E9E'; // Default gray
};

/**
 * Fetches jobs within X miles of a maid's address
 * @param {string} maidId - Maid profile ID
 * @param {Object} options - Query options
 * @param {number} options.radiusMiles - Search radius in miles (default: 25)
 * @param {string} options.status - Comma-separated status filter
 * @param {string} options.scheduledStartMin - Minimum scheduled start date
 * @param {string} options.scheduledStartMax - Maximum scheduled start date
 * @returns {Promise<Object>} Jobs near the maid with distance information
 */
export const fetchJobsNearMaid = async (maidId, options = {}) => {
  try {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    const params = {
      radiusMiles: options.radiusMiles || 25,
      status: options.status,
      scheduledStartMin: options.scheduledStartMin,
      scheduledStartMax: options.scheduledStartMax
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    const response = await axios.get(`${apiBase}/api/jobs/nearby-maid/${maidId}`, {
      headers,
      params
    });

    return response.data;
  } catch (error) {
    logger.log('Error fetching jobs near maid:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Failed to fetch jobs near technician. Please try again.'
    );
  }
};

export default {
  fetchJobs,
  fetchJobById,
  fetchJobsForMaid,
  fetchJobsForEmployee,
  fetchJobStats,
  fetchJobsForMapping,
  fetchEmployees,
  extractJobAddress,
  getJobStatusColor,
  fetchJobsNearMaid
};